import { Fragment, useMemo } from 'react';

const SAFE_LINK_SCHEMES = new Set(['http', 'https', 'mailto']);

function safeHref(url) {
  if (!url) return null;
  const trimmed = url.trim();
  const schemeMatch = trimmed.match(/^([a-z][a-z0-9+.-]*):/i);
  if (schemeMatch) {
    return SAFE_LINK_SCHEMES.has(schemeMatch[1].toLowerCase()) ? trimmed : null;
  }
  return trimmed;
}

function renderInlineMarkdown(text) {
  if (!text) return null;

  const tokens = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g);

  return tokens.filter(Boolean).map((token, index) => {
    if (token.startsWith('`') && token.endsWith('`')) {
      return (
        <code key={index} className="rounded bg-muted px-1 py-0.5 text-[0.85em] font-mono text-foreground">
          {token.slice(1, -1)}
        </code>
      );
    }

    if (token.startsWith('**') && token.endsWith('**')) {
      return <strong key={index}>{token.slice(2, -2)}</strong>;
    }

    if (token.startsWith('*') && token.endsWith('*')) {
      return <em key={index}>{token.slice(1, -1)}</em>;
    }

    const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const href = safeHref(linkMatch[2]);
      if (!href) {
        return <span key={index}>{linkMatch[1]}</span>;
      }
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline underline-offset-2"
        >
          {linkMatch[1]}
        </a>
      );
    }

    return token;
  });
}

function parseTableRow(line) {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) {
    return null;
  }

  const normalized = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
  const withoutTrailing = normalized.endsWith('|') ? normalized.slice(0, -1) : normalized;
  const cells = withoutTrailing.split('|').map((cell) => cell.trim());

  if (cells.length < 2 || cells.every((cell) => !cell)) {
    return null;
  }

  return cells;
}

function isTableSeparator(line, expectedColumns) {
  const cells = parseTableRow(line);
  if (!cells || cells.length !== expectedColumns) {
    return false;
  }

  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function renderMarkdown(text) {
  if (!text) return null;

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let index = 0;
  const startsTable = (value, nextValue) => {
    if (nextValue == null) return false;
    const headerCells = parseTableRow(value);
    if (!headerCells) return false;
    return isTableSeparator(nextValue.trim(), headerCells.length);
  };
  const startsNewBlock = (value, nextValue) => (
    value.startsWith('```') ||
    /^(#{1,6})\s+/.test(value) ||
    /^[-*+]\s+/.test(value) ||
    /^\d+\.\s+/.test(value) ||
    startsTable(value, nextValue)
  );

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith('```')) {
      const codeLines = [];
      index += 1;

      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }

      if (index < lines.length) {
        index += 1;
      }

      blocks.push(
        <pre key={`code-${blocks.length}`} className="overflow-x-auto rounded-lg bg-card px-3 py-2 font-mono text-xs text-foreground border border-border">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const HeadingTag = `h${Math.min(level + 2, 6)}`;
      blocks.push(
        <HeadingTag key={`heading-${blocks.length}`} className="font-semibold text-foreground">
          {renderInlineMarkdown(headingMatch[2])}
        </HeadingTag>
      );
      index += 1;
      continue;
    }

    if (/^[-*+]\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^[-*+]\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^[-*+]\s+/, ''));
        index += 1;
      }

      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-1">
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderInlineMarkdown(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ''));
        index += 1;
      }

      blocks.push(
        <ol key={`ol-${blocks.length}`} className="list-decimal pl-5 space-y-1">
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderInlineMarkdown(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    const headerCells = parseTableRow(trimmed);
    if (
      headerCells &&
      index + 1 < lines.length &&
      isTableSeparator(lines[index + 1].trim(), headerCells.length)
    ) {
      const bodyRows = [];
      index += 2;

      while (index < lines.length) {
        const rowCells = parseTableRow(lines[index].trim());
        if (!rowCells || rowCells.length !== headerCells.length) {
          break;
        }
        bodyRows.push(rowCells);
        index += 1;
      }

      blocks.push(
        <div key={`table-${blocks.length}`} className="overflow-x-auto">
          <table className="min-w-full border-collapse rounded-lg border border-border data-table">
            <thead className="bg-muted/50">
              <tr>
                {headerCells.map((cell, cellIndex) => (
                  <th key={cellIndex} className="border border-border px-3 py-2 font-semibold text-foreground">
                    {renderInlineMarkdown(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={rowIndex} className="align-top">
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="border border-border px-3 py-2 text-foreground/90">
                      {renderInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const paragraphLines = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !startsNewBlock(lines[index].trim(), lines[index + 1])
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }

    blocks.push(
      <p key={`p-${blocks.length}`} className="leading-6">
        {paragraphLines.map((paragraphLine, paragraphIndex) => (
          <Fragment key={paragraphIndex}>
            {paragraphIndex > 0 && <br />}
            {renderInlineMarkdown(paragraphLine)}
          </Fragment>
        ))}
      </p>
    );
  }

  return blocks;
}

export function MarkdownContent({ text, className = '' }) {
  const blocks = useMemo(() => (text ? renderMarkdown(text) : null), [text]);
  if (!blocks) return null;

  return (
    <div className={`space-y-3 text-sm ${className}`}>
      {blocks}
    </div>
  );
}

export default MarkdownContent;
